import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const FeaturedProjects = () => {
  const [hoveredProject, setHoveredProject] = useState(null);

  const projects = [
    {
      id: 1,
      title: "CodersHub Innovations",
      subtitle: "An 8-Division Tech Ecosystem — Founder",
      description: "An integrated tech ecosystem for Cameroon and West Africa — eight divisions (education, housing, talent, incubation, research, design, community, operations) under one shared platform.",
      image: "/images/projects/codershub-innovations.png",
      category: "SaaS / Platform",
      techStack: ["Node.js/Express", "Python/FastAPI", "React", "MongoDB Atlas", "Firebase Auth"],
      businessImpact: {
        divisions: "8",
        dataModels: "63+",
        communityMembers: "500+",
        role: "Founder"
      },
      features: [
        "Dual Node/FastAPI backend, 63+ models across 12 modules",
        "8-level role-based access control across every division",
        "n8n workflow automation wired into backend events",
        "Grew the CodersHub developer community to 500+ members",
        "Shared MongoDB Atlas database + unified API gateway"
      ],
      liveUrl: "https://codershublabs.com",
      githubUrl: "#",
      status: "Live",
      client: "Own Venture",
      duration: "Ongoing",
      year: "Ongoing"
    },
    {
      id: 2,
      title: "Reeyo",
      subtitle: "Multi-Country Delivery — CTO",
      description: "A technology-enabled delivery platform connecting vendors, riders, and consumers for food, groceries, and package delivery across Cameroon, Nigeria, Kenya, and Ghana. I serve as CTO.",
      image: "/images/projects/reeyo.png",
      category: "Mobile App",
      techStack: ["Node.js 20/TypeScript", "Express", "PostgreSQL", "Redis", "React Native (Expo)"],
      businessImpact: {
        appRating: "4.2★",
        avgDelivery: "~20 min",
        paymentProcessors: "5",
        role: "CTO"
      },
      features: [
        "Multi-vendor food, grocery & package delivery",
        "5 backend services + real-time socket server, 4 client apps",
        "Payment routing across 5 processors (CamPay, Paystack, M-Pesa, Flutterwave, Stripe)",
        "Wallet engine with Redis distributed locks",
        "Admin-controlled feature flags across all services"
      ],
      liveUrl: "https://usereeyo.com",
      githubUrl: "#",
      status: "Live",
      client: "Own Venture (CTO)",
      duration: "Ongoing",
      year: "Ongoing"
    },
    {
      id: 3,
      title: "TrustHaven",
      subtitle: "Cameroon's Rental Property Marketplace",
      description: "See the agent fee before you call, and displacement fees are banned for every verified agent — with real MTN MoMo / Orange Money payments running live in production.",
      image: "/images/projects/th.png",
      category: "Web & Mobile App",
      techStack: ["React Native (Expo)", "Node.js/Express", "Supabase Postgres", "Twilio/Infobip", "Fapshi"],
      businessImpact: {
        notificationChannels: "5",
        eventTypes: "18",
        paymentMode: "Production"
      },
      features: [
        "Verified listings with photos, video tours, and map coordinates",
        "Real MTN MoMo / Orange Money via Fapshi — production mode, not sandbox",
        "5-channel notifications (email, push, SMS, in-app, admin) across 18 event types",
        "KYC-gated provider upgrades and credit-based contact unlocks",
        "Dual auth: email OTP + Google Sign-In, phone OTP ready pending carrier verification"
      ],
      liveUrl: "https://gettrusthaven.com",
      githubUrl: "#",
      status: "Live",
      client: "STN Technologies Oy",
      duration: "Ongoing",
      year: "Ongoing"
    },
    {
      id: 4,
      title: "ClinchDate",
      subtitle: "Dating App — iOS, Android, 6 Languages",
      description: "A modern dating app combining swipe discovery with structured 7-minute video Mini Dates, AI icebreakers, and a safety-first design. 500,000+ members across 120 countries.",
      image: "/images/projects/clinchdate.png",
      category: "Mobile App",
      techStack: ["React Native (Expo)", "Node.js/Express/TypeScript", "MongoDB", "Socket.IO", "Twilio", "Firebase"],
      businessImpact: {
        members: "500K+",
        countries: "120",
        couplesMatched: "80K+",
        appRating: "4.9★"
      },
      features: [
        "Structured 7-minute video Mini Dates + AI icebreakers",
        "Published shared SDK (@clinchdate/api-client) across mobile, web, and admin",
        "Socket.IO chat/calls/presence, Twilio video/voice",
        "Global (PayPal) + regional (Fapshi/MoMo/Orange) payments",
        "Emergency contacts + reputation-score safety system"
      ],
      liveUrl: "https://clinchdate.com",
      githubUrl: "#",
      status: "Live",
      client: "STN Technologies Oy",
      duration: "Ongoing",
      year: "Ongoing"
    },
    {
      id: 5,
      title: "OfferBid",
      subtitle: "Hyper-Local Marketplace — Cofounder & Engineer",
      description: "A hyper-local second-hand marketplace for Buea, Cameroon, replacing chaotic WhatsApp/Facebook trading groups with structured, real-time offers instead of chat spam.",
      image: "/images/projects/offerbid.png",
      category: "Marketplace",
      techStack: ["NestJS", "Prisma", "PostgreSQL", "Redis", "BullMQ", "Socket.IO"],
      businessImpact: {
        offerExpiry: "24h",
        role: "Cofounder"
      },
      features: [
        "Real-time counter-bidding engine over Socket.IO",
        "BullMQ-backed 24-hour offer expiry and digest notifications",
        "Presigned S3 direct-upload — API never touches image bytes",
        "Tiered seller trust system with KYC identity verification",
        "Accepted deals hand off to WhatsApp for the actual transaction"
      ],
      liveUrl: "https://offerbid-api.onrender.com",
      githubUrl: "#",
      status: "Live",
      client: "Own Venture (Cofounder)",
      duration: "Ongoing",
      year: "Ongoing"
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="FolderOpen" size={20} className="text-primary" />
            <span className="text-sm font-brand-medium text-primary tracking-wide uppercase">Featured Work</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-brand-headline text-foreground mb-6">
            Innovations That Redefine
            <span className="block text-primary text-gradient-gold">Industries</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto font-brand-body">
            Each project represents a unique challenge solved with cutting-edge technology,
            delivering measurable results and exceptional user experiences.
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {projects?.map((project) => (
            <div
              key={project?.id}
              className="group relative bg-card rounded-2xl overflow-hidden border border-border hover:border-primary/30 transition-all duration-500 brand-shadow hover:brand-shadow-lg"
              onMouseEnter={() => setHoveredProject(project?.id)}
              onMouseLeave={() => setHoveredProject(null)}
            >
              {/* Project Image */}
              <div className="relative h-64 overflow-hidden">
                <Image
                  src={project?.image}
                  alt={project?.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-secondary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Status Badge */}
                <div className="absolute top-4 left-4">
                  <span className="inline-flex items-center px-3 py-1 bg-success/90 text-white text-xs font-brand-medium rounded-full">
                    <Icon name="Circle" size={8} className="mr-1 fill-current" />
                    {project?.status}
                  </span>
                </div>

                {/* Category Badge */}
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-background/90 text-foreground text-xs font-brand-medium rounded-full border border-border">
                    {project?.category}
                  </span>
                </div>

                {/* Hover Actions */}
                <div className={`absolute inset-0 flex items-center justify-center space-x-4 transition-all duration-300 ${
                  hoveredProject === project?.id ? 'opacity-100 visible' : 'opacity-0 invisible'
                }`}>
                  <a
                    href={project?.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-colors"
                    aria-label="View Live Project"
                  >
                    <Icon name="ExternalLink" size={20} />
                  </a>
                  <a
                    href={project?.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-background text-foreground rounded-full hover:bg-muted transition-colors"
                    aria-label="View Source Code"
                  >
                    <Icon name="Github" size={20} />
                  </a>
                </div>
              </div>

              {/* Project Content */}
              <div className="p-6">
                {/* Header */}
                <div className="mb-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-xl font-brand-subheading text-foreground group-hover:text-primary transition-colors">
                      {project?.title}
                    </h3>
                    <span className="text-sm text-text-secondary font-brand-body">
                      {project?.year}
                    </span>
                  </div>
                  <p className="text-sm text-primary font-brand-medium mb-3">
                    {project?.subtitle}
                  </p>
                  <p className="text-text-secondary font-brand-body leading-relaxed">
                    {project?.description}
                  </p>
                </div>

                {/* Tech Stack */}
                <div className="mb-6">
                  <h4 className="text-sm font-brand-medium text-foreground mb-2">Tech Stack</h4>
                  <div className="flex flex-wrap gap-2">
                    {project?.techStack?.map((tech, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-muted text-text-secondary text-xs font-brand-body rounded border border-border"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Business Impact */}
                <div className="mb-6">
                  <h4 className="text-sm font-brand-medium text-foreground mb-3">Business Impact</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(project?.businessImpact)?.map(([key, value], idx) => (
                      <div key={idx} className="text-center p-3 bg-muted rounded-lg">
                        <div className="text-lg font-brand-headline text-primary">
                          {value}
                        </div>
                        <div className="text-xs text-text-secondary capitalize font-brand-body">
                          {key}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Key Features */}
                {hoveredProject === project?.id && (
                  <div className="mb-6 animate-in slide-in-from-top duration-300">
                    <h4 className="text-sm font-brand-medium text-foreground mb-2">Key Features</h4>
                    <ul className="space-y-1">
                      {project?.features?.slice(0, 3)?.map((feature, idx) => (
                        <li key={idx} className="text-sm text-text-secondary flex items-start">
                          <Icon name="Check" size={14} className="text-success mr-2 mt-0.5 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Project Meta */}
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="text-sm text-text-secondary">
                    <span className="font-brand-medium">Client:</span> {project?.client}
                  </div>
                  <div className="text-sm text-text-secondary">
                    <span className="font-brand-medium">Duration:</span> {project?.duration}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All Projects CTA */}
        <div className="text-center">
          <Link to="/projectspage">
            <Button
              variant="outline"
              size="lg"
              iconName="ArrowRight"
              iconPosition="right"
              iconSize={20}
              className="border-primary/30 text-primary hover:bg-primary/10"
            >
              View All Projects
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProjects;